// src/components/forms/DropdownMulti.tsx
import { useState, useRef, useEffect } from "react";
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { Option } from "./types";

interface Props<T = string> {
  label: string;
  options: Option<T>[];
  value?: T[];
  onChange: (val: T[]) => void;
  placeholder?: string;
  className?: string;
  maxItems?: number;
}

export const DropdownMulti = <T extends string | number>({
  label,
  options,
  value,
  onChange,
  placeholder,
  className,
  maxItems
}: Props<T>) => {

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedItems = (value || []).filter((v) =>
    options.some(opt => opt.value === v)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered =
    query === ""
      ? options
      : options.filter((opt) =>
          opt.label.toLowerCase().includes(query.toLowerCase())
        );

  const isLimitReached =
    maxItems !== undefined && selectedItems.length >= maxItems;

  const clean = (vals: T[]) =>
    vals.filter(v => v !== "" && v !== null && v !== undefined);

  const toggle = (val: T) => {
    if (!val) return;

    const exists = selectedItems.includes(val);

    if (exists) {
      onChange(clean(selectedItems.filter(v => v !== val)));
    } else {
      if (!isLimitReached) {
        onChange(clean([...selectedItems, val]));
      }
    }
  };

  return (
    <div className={`flex flex-col gap-2 mt-7 ${className}`} ref={dropdownRef}>
      {/* Premium Upgraded Label Placement & Spacing */}
      <div className="flex items-center justify-between px-1">
        <span className="text-sm font-bold uppercase text-slate-200/70 dark:text-indigo-200 ml-1">
          {label}
        </span>
        {maxItems !== undefined && (
          <span className="text-[12px] font-medium tracking-wider text-white-500/80 bg-indigo-50/50 dark:bg-indigo-950/30 px-2 py-0.5 rounded-full border border-indigo-100/30">
            {selectedItems.length} / {maxItems} max
          </span>
        )}
      </div>

      <div className="relative group">
        {/* Glow match with single */}
        <div className={`absolute -inset-0.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl blur-[2px] transition duration-300 ${isOpen ? "opacity-100" : "opacity-20 group-hover:opacity-40"}`} />

        <div
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-full min-h-[44px] cursor-pointer rounded-xl bg-white dark:bg-zinc-900 px-4 py-2 flex items-center justify-between border border-zinc-200 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 focus:outline-none transition-all duration-200"
        >
          <div className="flex flex-wrap gap-1.5">
            {selectedItems.length > 0 ? (
              selectedItems.map((v) => {
                const option = options.find((o) => o.value === v);
                if (!option) return null;

                return (
                  <span key={String(v)} className="flex items-center gap-1 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-xs font-bold rounded-md border border-indigo-200 dark:border-indigo-800">
                    {option.label}
                    <XMarkIcon
                      className="w-3.5 h-3.5 cursor-pointer hover:text-pink-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(v);
                      }}
                    />
                  </span>
                );
              })
            ) : (
              <span className="text-zinc-400">
                {placeholder || "Select options..."}
              </span>
            )}
          </div>

          <ChevronUpDownIcon className={`w-5 h-5 transition-transform duration-200 ${isOpen ? "text-pink-500 rotate-180" : "text-indigo-400"}`} />
        </div>

        {isOpen && (
          <div className="absolute mt-2 w-full bg-black dark:bg-zinc-900 shadow-2xl rounded-xl max-h-64 overflow-hidden z-50 border border-zinc-100 dark:border-zinc-800 focus:outline-none">

            {/* Search Match */}
            <div className="p-2 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-2 bg-zinc-50/50 dark:bg-zinc-800/50">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="w-full bg-transparent border-none focus:ring-0 text-sm focus:outline-none"
              />
            </div>

            {/* Options List Layout Match */}
            <div className="overflow-auto max-h-48 p-1 space-y-0.5">
              {filtered.length > 0 ? (
                filtered.map((opt) => {
                  const isSelected = selectedItems.includes(opt.value);
                  const isDisabled = isLimitReached && !isSelected;

                  return (
                    <div
                      key={String(opt.value)}
                      onClick={() => !isDisabled && toggle(opt.value)}
                      className={`
                        cursor-pointer px-4 py-2.5 mx-1 my-0.5 rounded-lg transition-all duration-150 
                        ${isDisabled 
                          ? "opacity-30 cursor-not-allowed text-zinc-500" 
                          : isSelected 
                            ? "bg-white-800 text-cyan-400 font-bold border border-cyan-500/30 hover:bg-gradient-to-r hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 hover:text-white" 
                            : "text-slate-200 dark:text-zinc-600 hover:bg-gradient-to-r hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 hover:text-white hover:font-semibold"
                        }
                      `}
                    >
                      <div className="flex justify-between items-center">
                        <span className="truncate">{opt.label}</span>
                        {isSelected && (
                          <CheckIcon className="w-5 h-5 text-cyan-400 group-hover:text-white" />
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-6 text-center text-sm text-zinc-500">
                  No results found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};